import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { SidebarList } from './SidebarList';
import { DetailPane } from './DetailPane';

const darkThemeBg = '#0d1117';
const panelBg = 'rgba(22, 27, 34, 0.75)';
const accentColor = '#58a6ff';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: darkThemeBg,
      paper: panelBg,
    },
    text: {
      primary: '#e6edf3',
      secondary: '#8b949e',
    },
  },
});

export default function WsDebugger() {
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = newest at bottom, 'desc' = newest at top
  const [isFlattened, setIsFlattened] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const bcRef = useRef(null);
  const listEndRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [sidebarWidth, setSidebarWidth] = useState(40);
  const isDragging = useRef(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const newWidth = (e.clientX / window.innerWidth) * 100;
    if (newWidth > 20 && newWidth < 80) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    if (sortOrder === 'asc' && listEndRef.current) {
      listEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isFlattened, sortOrder]);

  useEffect(() => {
    // Set dark body background for this specific tab
    document.body.style.backgroundColor = darkThemeBg;

    try {
      bcRef.current = new BroadcastChannel('ws-network-debugger');
      bcRef.current.onmessage = (event) => {
        const { type, timestamp, data, triggerUrl } = event.data;

        setMessages((prev) => {
          const newMessages = [...prev];
          const existingIdx = newMessages.findIndex((m) => m.id === data.id);

          if (existingIdx >= 0) {
            const msg = { ...newMessages[existingIdx] };
            if (type === 'request') {
              msg.reqTimestamp = timestamp;
              msg.method = data.method;
              msg.params = data.params;
              msg.resource = data.params?.resource || msg.resource;
              msg.triggerUrl = triggerUrl || msg.triggerUrl;
            } else if (type === 'response') {
              msg.resTimestamp = timestamp;
              msg.result = data.result;
              msg.error = data.error;
              msg.status = data.error ? 'error' : 'success';
              if (msg.result?.resource) {
                msg.resource = msg.result.resource;
              }
              msg.triggerUrl = triggerUrl || msg.triggerUrl;
            }
            if (msg.reqTimestamp && msg.resTimestamp) {
              msg.duration = msg.resTimestamp - msg.reqTimestamp;
            }
            newMessages[existingIdx] = msg;
          } else {
            const newMsg = {
              id: data.id,
              status: type === 'response' ? (data.error ? 'error' : 'success') : 'pending',
              resource: data.params?.resource || data.result?.resource || 'Unknown Resource',
              method: type === 'request' ? data.method : undefined,
              params: type === 'request' ? data.params : undefined,
              result: type === 'response' ? data.result : undefined,
              error: type === 'response' ? data.error : undefined,
              triggerUrl: triggerUrl,
            };
            if (type === 'request') newMsg.reqTimestamp = timestamp;
            if (type === 'response') newMsg.resTimestamp = timestamp;
            newMessages.push(newMsg); // Append new items
          }
          return newMessages;
        });
      };
    } catch (e) {
      console.error('Failed to initialize BroadcastChannel', e);
    }

    return () => {
      if (bcRef.current) bcRef.current.close();
      document.body.style.backgroundColor = '';
    };
  }, []);

  const handleClear = () => {
    setMessages([]);
    setSelectedId(null);
  };

  const selectedMsg = messages.find((m) => m.id === selectedId);

  const filteredMessages = messages.filter((msg) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      msg.resource?.toLowerCase().includes(query) ||
      msg.method?.toLowerCase().includes(query) ||
      msg.id?.toLowerCase().includes(query) ||
      msg.triggerUrl?.toLowerCase().includes(query) ||
      (msg.params && JSON.stringify(msg.params).toLowerCase().includes(query)) ||
      (msg.result && JSON.stringify(msg.result).toLowerCase().includes(query)) ||
      (msg.error && JSON.stringify(msg.error).toLowerCase().includes(query))
    );
  });

  let displayItems = [];
  if (isFlattened) {
    filteredMessages.forEach((msg) => {
      if (msg.reqTimestamp !== undefined) {
        displayItems.push({
          id: msg.id,
          uniqueKey: `${msg.id}-req`,
          type: 'request',
          timestamp: msg.reqTimestamp,
          status: 'request',
          resource: msg.resource,
          method: msg.method,
          triggerUrl: msg.triggerUrl,
          duration: undefined,
          originalMsg: msg,
        });
      }
      if (msg.resTimestamp !== undefined) {
        displayItems.push({
          id: msg.id,
          uniqueKey: `${msg.id}-res`,
          type: 'response',
          timestamp: msg.resTimestamp,
          status: msg.error ? 'error' : 'success',
          resource: msg.resource,
          method: msg.method,
          triggerUrl: msg.triggerUrl,
          duration: msg.duration,
          originalMsg: msg,
        });
      }
      if (msg.reqTimestamp === undefined && msg.resTimestamp === undefined) {
        displayItems.push({
          id: msg.id,
          uniqueKey: `${msg.id}-unknown`,
          type: 'unknown',
          timestamp: Date.now(),
          status: msg.status,
          resource: msg.resource,
          method: msg.method,
          triggerUrl: msg.triggerUrl,
          duration: undefined,
          originalMsg: msg,
        });
      }
    });

    displayItems.sort((a, b) => {
      const diff = a.timestamp - b.timestamp;
      return sortOrder === 'asc' ? diff : -diff;
    });
  } else {
    displayItems = filteredMessages.map((msg) => ({
      id: msg.id,
      uniqueKey: msg.id,
      type: 'grouped',
      timestamp: msg.reqTimestamp || msg.resTimestamp,
      status: msg.status,
      resource: msg.resource,
      method: msg.method,
      triggerUrl: msg.triggerUrl,
      duration: msg.duration,
      originalMsg: msg,
    }));

    if (sortOrder === 'desc') {
      displayItems.reverse();
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ display: 'flex', height: '100vh', bgcolor: darkThemeBg, color: '#e6edf3', fontFamily: 'Inter, sans-serif' }}>
        <SidebarList
          messages={messages}
          displayMessages={displayItems}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          sidebarWidth={sidebarWidth}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isFlattened={isFlattened}
          setIsFlattened={setIsFlattened}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          handleClear={handleClear}
          windowWidth={windowWidth}
          listEndRef={listEndRef}
        />

        {/* Resizer */}
        {selectedId && (
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              width: '4px',
              cursor: 'col-resize',
              bgcolor: 'transparent',
              transition: 'background-color 0.2s',
              '&:hover, &:active': { bgcolor: accentColor },
              zIndex: 10,
            }}
          />
        )}

        {/* Detail Pane */}
        {selectedId && (
          <DetailPane
            selectedMsg={selectedMsg}
            setSelectedId={setSelectedId}
            sidebarWidth={sidebarWidth}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}
